import { FormGenerator } from "@gsa/afp-shared-form-utils";
import generateAdditionalRequirementForm from "./additional-requirement-form";
import { useSelector } from "react-redux";
import { useCreateRequisitionAttachment, useDeleteRequisitionAttachment, useUpdateRequisitionAttachment } from "../../../../../../requests/leasing";
import { useCreateMetadataRecord, useDeleteMetadataRecord } from "../../../../../../requests/vehicle-referral";
import axios from 'axios';
import { forwardRef, useEffect, useRef } from "react";
import { generateAdditionalRequirementSchema } from "./additional-requirement-schema";
import { useDispatch } from "react-redux";
import { setAdditionalRequirements } from "../../../../../../reducers/leasing";

export default forwardRef(function RequirementForm({
    requirement,
    attachmentTypes,
    index,
    onChange,
}, ref) {
    const additionalRequirements = useSelector((state) => state.leasingReducer.additionalRequirements);
    const dispatch = useDispatch();
    const requisitionId = useSelector((state) => state.leasingReducer.requisitionId);
    const [createRequisitionAttachment, { loading: uploading }] = useCreateRequisitionAttachment();
    const [createMetadataRecord, { loading: uploadingMetadata }] = useCreateMetadataRecord();
    const [deleteMetadataRecord, { loading: deletingMetadataRecord }] = useDeleteMetadataRecord();
    const [deleteRequisitionAttachment, { loading: deletingRequisitionAttachment }] = useDeleteRequisitionAttachment();
    const [updateRequisitionAttachment, { loading: saving }] = useUpdateRequisitionAttachment();

    const handleUpload = async (data, { addFileToList }) => {
        const metadataInput = {
            name: data.file.name,
            size: data.file.size,
            fileMimeType: data.file.type,
            description: data.note,
            fileLocation: `requisition_attachments/${new Date().getFullYear()}/${requisitionId}/${data.file.name}`,
            linkedEntities: {
                replacementAttachmentTypeId: data.category,
                requisitionId
            },
            docSource: 'UserUploaded',
            docStore: window.AFP_CONFIG.attachment_bucket,
        };

        let metadata = null;
        try {
            const metadataRecord = await createMetadataRecord({
                variables: { input: metadataInput },
            });

            metadata = metadataRecord.data;
        } catch ({ error }) {
            throw error;
        }

        try {
            await axios.put(metadata.signedUrl, data.file, {
                headers: {
                    'Content-Type': data.file.type,
                    'Content-Disposition': `attachment; filename="${data.file.name}"`,
                },
            });
        } catch (error) {
            try {
                await deleteMetadataRecord({
                    variables: {
                        deleteMetadataRecordId: metadata.id,
                    },
                });
            } catch ({ error }) {
                throw error;
            }

            throw error;
        }

        try {
            const { data: attachment } = await createRequisitionAttachment({
                variables: {
                    input: {
                        requisitionId,
                        additionalRequirementId: requirement.requisitionAdditionalRequirementId,
                        name: data.file.name,
                        status: 0,
                        attachmentTypeId: data.category,
                        description: data.note,
                        size: data.file.size,
                        fileMimeType: data.file.type,
                        metadataId: metadata.id,
                        signedUrl: metadata.signedUrl,
                    },
                },
            });

            addFileToList({
                ...attachment,
                note: attachment.description,
                category: attachment.attachmentTypeId,
                id: attachment.requisitionAttachmentId,
            });
        } catch ({ error }) {
            throw error;
        }
    };
    const handleSave = async (data, { updateFileInList }) => {
        try {
            const { data: attachment } = await updateRequisitionAttachment({
                variables: {
                    attachmentId: data.file.id,
                    input: {
                        attachmentTypeId: data.category,
                        description: data.note,
                    },
                },
            });

            updateFileInList({
                ...attachment,
                note: attachment.description,
                category: attachment.attachmentTypeId,
                id: attachment.requisitionAttachmentId,
            });
        } catch ({ error }) {
            throw error;
        }
    };

    const handleDelete = async (data, { removeFileFromList }) => {
        try {
            await deleteRequisitionAttachment({
                variables: {
                    attachmentId: data.id,
                },
            });

            await deleteMetadataRecord({
                variables: {
                    deleteMetadataRecordId: data.metadataId,
                },
            });

            removeFileFromList({
                ...data,
                id: data.id,
            });
        } catch ({ error }) {
            throw error;
        }
    };

    const handleEditAction = (data, { openFileModal }) => {
        openFileModal();
    };

    const handleDeleteAction = async (data, { openConfirmDeleteModal }) => {
        openConfirmDeleteModal();
    };

    const handleRequirementUpdate = (_, { type, values } = {}) => {
        if (type !== 'change' || !values) return;

        const updatedValues = Object.fromEntries(
            Object.entries(values).map(([key, value]) => {
                const newKey = key.replace(`-${index}`, '');
                return [newKey, value];
            })
        );

        const requirement = additionalRequirements[index];
        const updatedRequirement = {
            ...requirement,
            requisitionAdditionalRequirementId: updatedValues.id,
            description: updatedValues.description,
            priceEstimate: updatedValues.priceEstimate,
        }

        const newRequirements = [...additionalRequirements];
        newRequirements[index] = updatedRequirement;

        dispatch(setAdditionalRequirements(newRequirements));
    };

    useEffect(() => {
        const subscription = ref.current?.watch(handleRequirementUpdate)
        return () => { subscription?.unsubscribe(); }
    }, [ref.current])

    const [defaultValues, formContent] = generateAdditionalRequirementForm({
        requirement,
        handleUpload,
        handleSave,
        handleDelete,
        handleEditAction,
        handleDeleteAction,
        uploading: uploading || uploadingMetadata || deletingRequisitionAttachment,
        saving: false,
        deleting: deletingRequisitionAttachment || deletingMetadataRecord,
        attachmentTypes,
        index,
    })

    return (
        <FormGenerator
            schema={generateAdditionalRequirementSchema(index)}
            useFormElements={false}
            content={formContent}
            defaultValues={defaultValues}
            ref={ref}
            useFormProps={{
                mode: 'all',
            }}
        />
    )
});